import { LIFECYCLE_STATUS, type LifecycleStatus } from "../types/common";
import { V2_COLORS } from "./colors";

type StatusVariant = "success" | "warning" | "error" | "info" | "default";

type StatusMeta = {
  label: string;
  /** Label shown to internal users (AP/VM/Finance). Falls back to label if not set. */
  internalLabel?: string;
  /** Label shown to vendor users. Falls back to label if not set. */
  vendorLabel?: string;
  variant: StatusVariant;
  icon: string;
  color: string;
  bgColor: string;
  borderColor?: string;
};

// Color pairs used across statuses — single source of truth
const STATUS_COLORS = {
  info: { color: V2_COLORS.info, bgColor: V2_COLORS.infoBg },
  success: { color: "#FFFFFF", bgColor: V2_COLORS.success },
  successSoft: { color: V2_COLORS.successSoft, bgColor: V2_COLORS.successSoftBg, borderColor: V2_COLORS.successBg },
  warning: { color: "#FFFFFF", bgColor: V2_COLORS.warning },
  error: { color: V2_COLORS.error, bgColor: V2_COLORS.errorBg, borderColor: "#F8D7D4" },
  default: { color: V2_COLORS.textSecondary, bgColor: V2_COLORS.bgMuted },
} as const;

// Old v1 invoice states — mapped to colors so they don't all render gray.
// Display label comes from invoice_status.label via the displayLabel prop on StatusBadge.
const OLD_STATUS_META: Record<string, StatusMeta> = {
  pending: { label: "Pending", variant: "warning", icon: "pending", ...STATUS_COLORS.warning },
  partial_invoice: { label: "Partial Invoice", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  full_invoice: { label: "Full Invoice", variant: "info", icon: "done", ...STATUS_COLORS.info },
  exception: { label: "Exception", variant: "error", icon: "close", ...STATUS_COLORS.error },
  rejected: { label: "Rejected", variant: "error", icon: "close", ...STATUS_COLORS.error },
  revised: { label: "Revised", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  revision_required: { label: "Revision Required", variant: "warning", icon: "pending", ...STATUS_COLORS.warning },
  submission_requested: { label: "Submission Requested", variant: "warning", icon: "pending", ...STATUS_COLORS.warning },
  completed: { label: "Completed", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
  approved: { label: "Approved", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
};

export const STATUS_META: Record<LifecycleStatus, StatusMeta> = {
  [LIFECYCLE_STATUS.UPLOADED]: { label: "Uploaded", internalLabel: "Pending submission", vendorLabel: "Pending submission", variant: "success", icon: "file_upload", ...STATUS_COLORS.success },
  [LIFECYCLE_STATUS.OCR_IN_PROGRESS]: { label: "Processing", internalLabel: "Pending submission", vendorLabel: "Pending submission", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  [LIFECYCLE_STATUS.OCR_COMPLETED]: { label: "OCR Completed", variant: "info", icon: "done", ...STATUS_COLORS.info },
  [LIFECYCLE_STATUS.OCR_FAILED]: { label: "OCR Failed", internalLabel: "Revision Required - Vendor", vendorLabel: "Revision required - Vendor", variant: "error", icon: "close", ...STATUS_COLORS.error },
  [LIFECYCLE_STATUS.VALIDATION_IN_PROGRESS]: { label: "Validating", internalLabel: "Pending submission", vendorLabel: "Pending submission", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  [LIFECYCLE_STATUS.VALIDATION_SUCCESS]: { label: "Validation Passed", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
  [LIFECYCLE_STATUS.VALIDATION_FAILED]: { label: "Validation Failed", internalLabel: "Validation Failed - VM", vendorLabel: "Submitted", variant: "error", icon: "close", ...STATUS_COLORS.error },
  [LIFECYCLE_STATUS.GRN_PENDING]: { label: "GRN Pending", internalLabel: "GRN Pending", vendorLabel: "Submitted", variant: "warning", icon: "pending", ...STATUS_COLORS.warning },
  [LIFECYCLE_STATUS.GRN_MATCHED]: { label: "GRN Matched", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
  [LIFECYCLE_STATUS.THREE_WAY_MATCH_PENDING]: { label: "Matching", internalLabel: "GRN Pending", vendorLabel: "Submitted", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  [LIFECYCLE_STATUS.MATCH_EXCEPTION]: { label: "Exception", internalLabel: "Validation failed", vendorLabel: "Submitted", variant: "error", icon: "close", ...STATUS_COLORS.error },
  [LIFECYCLE_STATUS.UNDER_REVIEW]: { label: "Under Review", internalLabel: "Finance Approval Pending", vendorLabel: "Submitted", variant: "warning", icon: "find_in_page", ...STATUS_COLORS.warning },
  [LIFECYCLE_STATUS.APPROVED]: { label: "Approved", internalLabel: "Approved", vendorLabel: "Approved", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
  [LIFECYCLE_STATUS.REJECTED]: { label: "Rejected", internalLabel: "Rejected", vendorLabel: "Revision Required", variant: "error", icon: "close", ...STATUS_COLORS.error },
  [LIFECYCLE_STATUS.EXPORT_READY]: { label: "Export Ready", internalLabel: "Approved", vendorLabel: "Approved", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
  [LIFECYCLE_STATUS.SENT_TO_D365]: { label: "Sent to D365", internalLabel: "Approved", vendorLabel: "Approved", variant: "info", icon: "sync", ...STATUS_COLORS.info },
  [LIFECYCLE_STATUS.D365_FAILED]: { label: "D365 Failed", internalLabel: "ERP upload Failed", vendorLabel: "Approved", variant: "error", icon: "close", ...STATUS_COLORS.error },
  [LIFECYCLE_STATUS.EXPORTED]: { label: "Exported", internalLabel: "ERP Success", vendorLabel: "Approved", variant: "success", icon: "done", ...STATUS_COLORS.successSoft },
};

/** Convert SCREAMING_SNAKE_CASE to Title Case (e.g. "VALIDATION_FAILED" → "Validation Failed") */
function formatStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const UNKNOWN_META: StatusMeta = { label: "Unknown", variant: "default", icon: "help_outline", ...STATUS_COLORS.default };

export function getStatusMeta(status: LifecycleStatus): StatusMeta {
  if (!status) return UNKNOWN_META;
  return STATUS_META[status] ?? OLD_STATUS_META[status] ?? { label: formatStatusLabel(status), variant: "default", icon: "help_outline", ...STATUS_COLORS.default };
}

/** PO originating systems, as sent by the BE in `po_source`. */
export const PO_SOURCE = {
  FINS: "FINS",
  IMS: "IMS",
  ZOHO: "ZOHO",
} as const;

/** Union of the valid `po_source` values, e.g. `"FINS" | "IMS" | "ZOHO"`. */
export type PoSource = (typeof PO_SOURCE)[keyof typeof PO_SOURCE];

/**
 * True when a PO originates from FINS. Case-normalizes defensively: the ADR
 * pins `po_source` to uppercase, but a strict match would silently drift if the
 * BE ever emitted a different case (e.g. "Fins"). Shared by the three FINS-aware
 * call sites — the label override (getStatusLabel), the "EXCEEDS LIMIT" chip
 * suppression (InvoiceDetail's showExceedsLimit), and the ThreeWayMatchCard
 * isFins prop — so they can't disagree.
 */
export function isFinsSource(poSource?: string | null): boolean {
  return poSource?.toUpperCase() === PO_SOURCE.FINS;
}

/**
 * Internal-label overrides for FINS (services) POs. A FINS PO never has a GRN
 * and its first-touch approver is Ops, so the GRN/Finance-flavored internal
 * labels are wrong for the states a FINS invoice passes through: it awaits the
 * two-way VALUE match (not a GRN) in THREE_WAY_MATCH_PENDING, a MATCH_EXCEPTION
 * means the PO value ceiling was breached (validation itself passed), and
 * UNDER_REVIEW awaits Ops — not Finance — approval (mirrors the v1 label the BE
 * synthesizes, ENG-19487). Vendors keep the vendorLabel ("Submitted").
 */
const FINS_INTERNAL_LABELS: Partial<Record<LifecycleStatus, string>> = {
  [LIFECYCLE_STATUS.THREE_WAY_MATCH_PENDING]: "Matching",
  [LIFECYCLE_STATUS.MATCH_EXCEPTION]: "PO Value Mismatch",
  [LIFECYCLE_STATUS.UNDER_REVIEW]: "OPS Approval Pending",
};

/**
 * Returns the role-appropriate display label for a lifecycle status.
 * Internal users see granular labels (e.g. "Finance Approval Pending").
 * Vendor users see simplified labels (e.g. "Submitted").
 *
 * `poSource` (the PO's originating system) lets the label branch by source —
 * see FINS_INTERNAL_LABELS for the FINS-specific internal labels.
 */
export function getStatusLabel(status: LifecycleStatus, isInternal: boolean, poSource?: string | null): string {
  if (isInternal && isFinsSource(poSource)) {
    const finsLabel = FINS_INTERNAL_LABELS[status];
    if (finsLabel) return finsLabel;
  }
  const meta = getStatusMeta(status);
  return isInternal ? (meta.internalLabel ?? meta.label) : (meta.vendorLabel ?? meta.label);
}

/**
 * lifecycle_status values for which the Edit action is permitted (mirrors backend CEL rule).
 * Shared between InvoiceDetailsV2.js (per-PO table) and InvoiceTable.tsx (Invoice Details tab)
 * so both surfaces stay in sync when statuses are added or removed.
 *
 * v1 invoices (lifecycle_status === null) are excluded — they will be migrated before v2 is enabled.
 */
export const EDITABLE_LIFECYCLE_STATUSES = new Set<string>([
  "OCR_COMPLETED",
  "VALIDATION_FAILED",
  "GRN_PENDING",
  "GRN_MATCHED",
  "THREE_WAY_MATCH_PENDING",
  "MATCH_EXCEPTION",
  "UNDER_REVIEW",
]);
